import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './AtrIndicatorTable.css';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from './config';
import { useWorkspace } from './workspace/WorkspaceContext';

const SELECT_ALL = '__ALL__';
const TIME_FRAME_OPTIONS = ['5', '15', '30', '60', '240', '720', '1440'];
const CANDLE_COUNT_OPTIONS = ['1', '2', '5', '10'];
const LIMIT_OPTIONS = ['100', '500', '1000'];
const COLUMNS = [
  { key: 'symbol', label: 'Symbol', type: 'select' },
  { key: 'timeFrame', label: 'TF', type: 'select' },
  { key: 'calculatedCandleCloseTime', label: 'Candle Close' },
  { key: 'atr14', label: 'ATR 14' },
  { key: 'atr14Percent', label: 'ATR 14 %' },
  { key: 'open', label: 'Open' },
  { key: 'high', label: 'High' },
  { key: 'low', label: 'Low' },
  { key: 'close', label: 'Close' },
  { key: 'volume', label: 'Volume' },
];

const getId = (row) => row.id || {};

const fmt = (value) => {
  if (value === undefined || value === null || value === '') return '-';
  return value;
};

function AtrIndicatorTable() {
  const { workspace } = useWorkspace();
  const [rows, setRows] = useState([]);
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [selectedSymbols, setSelectedSymbols] = useState([SELECT_ALL]);
  const [selectedTimeFrames, setSelectedTimeFrames] = useState([SELECT_ALL]);
  const [candlesPerSymbolTimeFrame, setCandlesPerSymbolTimeFrame] = useState('1');
  const [limit, setLimit] = useState('500');
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(buildScopedApiUrl('/api/bot/ui/get/getRecentActiveAlertSymbols', workspace), {
      headers: getApiHeaders(),
    })
      .then((response) => response.json())
      .then((data) => {
        setSymbolOptions(Array.from(new Set(ensureArray(data).filter(Boolean))).sort());
      })
      .catch((fetchError) => {
        console.error(fetchError);
        setSymbolOptions([]);
      });
  }, [workspace]);

  const normalizeMultiSelect = (selectedValues) => {
    if (selectedValues.includes(SELECT_ALL) || selectedValues.length === 0) {
      return [SELECT_ALL];
    }
    return selectedValues;
  };

  const handleSymbolsChange = (event) => {
    setSelectedSymbols(normalizeMultiSelect(Array.from(event.target.selectedOptions, (option) => option.value)));
  };

  const handleTimeFramesChange = (event) => {
    setSelectedTimeFrames(normalizeMultiSelect(Array.from(event.target.selectedOptions, (option) => option.value)));
  };

  const fetchCombos = useMemo(() => {
    const symbols = selectedSymbols.includes(SELECT_ALL) ? [null] : selectedSymbols;
    const timeFrames = selectedTimeFrames.includes(SELECT_ALL) ? [null] : selectedTimeFrames;

    return symbols.flatMap((selectedSymbol) =>
      timeFrames.map((selectedTimeFrame) => ({
        symbol: selectedSymbol,
        timeFrame: selectedTimeFrame,
      }))
    );
  }, [selectedSymbols, selectedTimeFrames]);

  const fetchRows = useCallback(() => {
    setLoading(true);
    setError('');

    Promise.all(
      fetchCombos.map(({ symbol, timeFrame }) =>
        fetch(buildScopedApiUrl('/api/bot/ui/atr/latest', workspace, {
          symbol,
          timeFrame,
          candlesPerSymbolTimeFrame,
          limit,
        }), {
          headers: getApiHeaders(),
        }).then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch ATR values: ${response.status}`);
          }
          return response.json();
        })
      )
    )
      .then((responses) => {
        const dedupedRows = new Map();
        responses.flatMap((data) => ensureArray(data)).forEach((row) => {
          const id = getId(row);
          const key = [
            id.exchange,
            id.marketType,
            id.symbol,
            id.timeFrame,
            id.calculatedCandleCloseTime,
          ].join('|');
          dedupedRows.set(key, row);
        });
        const safeRows = Array.from(dedupedRows.values());
        setRows(safeRows);
      })
      .catch((fetchError) => {
        console.error(fetchError);
        setError(fetchError.message || 'Unable to fetch ATR values');
        setRows([]);
      })
      .finally(() => setLoading(false));
  }, [candlesPerSymbolTimeFrame, fetchCombos, limit, workspace]);

  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      const id = getId(row);
      const flatRow = {
        symbol: id.symbol,
        timeFrame: id.timeFrame,
        calculatedCandleCloseTime: id.calculatedCandleCloseTime,
        atr14: row.atr14,
        atr14Percent: row.atr14Percent,
        open: row.open,
        high: row.high,
        low: row.low,
        close: row.close,
        volume: row.volume,
      };

      return COLUMNS.every(({ key }) => {
        const filter = (filters[key] || '').trim().toLowerCase();
        if (!filter || filter === 'all') return true;
        return String(flatRow[key] ?? '').toLowerCase().includes(filter);
      });
    });
  }, [filters, rows]);

  const tableSymbolOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => getId(row).symbol).filter(Boolean))).sort(),
    [rows]
  );

  const tableTimeFrameOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => getId(row).timeFrame).filter(Boolean))).sort((a, b) => Number(a) - Number(b)),
    [rows]
  );

  const clearFilters = () => {
    setFilters({});
  };

  return (
    <div className="atr-values-wrapper">
      <div className="atr-values-header">
        <div className="atr-values-title">
          <h2>ATR Values</h2>
          <p>Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong></p>
        </div>
        <div className="atr-values-toolbar">
          <button type="button" onClick={fetchRows} disabled={loading}>
            {loading ? 'Loading...' : 'Fetch ATR'}
          </button>
          <button
            type="button"
            className="atr-values-clear-button"
            onClick={clearFilters}
          >
            Reset Filters
          </button>
        </div>
      </div>

      <div className="atr-values-fetch-panel">
        <label className="atr-values-fetch-field atr-values-fetch-field--wide">
          Symbols
          <select multiple value={selectedSymbols} onChange={handleSymbolsChange}>
            <option value={SELECT_ALL}>Select All</option>
            {symbolOptions.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <span>{selectedSymbols.includes(SELECT_ALL) ? 'All symbols selected' : `${selectedSymbols.length} selected`}</span>
        </label>

        <label className="atr-values-fetch-field">
          Time Frames
          <select multiple value={selectedTimeFrames} onChange={handleTimeFramesChange}>
            <option value={SELECT_ALL}>Select All</option>
            {TIME_FRAME_OPTIONS.map((tf) => (
              <option key={tf} value={tf}>{tf}</option>
            ))}
          </select>
          <span>{selectedTimeFrames.includes(SELECT_ALL) ? 'All time frames selected' : `${selectedTimeFrames.length} selected`}</span>
        </label>

        <label className="atr-values-fetch-field">
          Candles Per Symbol/TF
          <select value={candlesPerSymbolTimeFrame} onChange={(event) => setCandlesPerSymbolTimeFrame(event.target.value)}>
            {CANDLE_COUNT_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>

        <label className="atr-values-fetch-field">
          Rows
          <select value={limit} onChange={(event) => setLimit(event.target.value)}>
            {LIMIT_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="atr-values-status atr-values-status--error">{error}</div>}

      <div className="atr-values-table-shell">
        <table className="atr-values-table">
          <thead>
            <tr>
              {COLUMNS.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
            <tr className="atr-values-filter-row">
              {COLUMNS.map((column) => (
                <th key={`${column.key}-filter`}>
                  {column.key === 'symbol' ? (
                    <select
                      value={filters.symbol || 'ALL'}
                      onChange={(event) => setFilters((current) => ({ ...current, symbol: event.target.value }))}
                    >
                      <option value="ALL">All</option>
                      {tableSymbolOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : column.key === 'timeFrame' ? (
                    <select
                      value={filters.timeFrame || 'ALL'}
                      onChange={(event) => setFilters((current) => ({ ...current, timeFrame: event.target.value }))}
                    >
                      <option value="ALL">All</option>
                      {tableTimeFrameOptions.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      value={filters[column.key] || ''}
                      onChange={(event) => setFilters((current) => ({ ...current, [column.key]: event.target.value }))}
                      placeholder={`Filter ${column.label}`}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan="10">No ATR rows found.</td>
              </tr>
            )}
            {visibleRows.map((row) => {
              const id = getId(row);
              return (
                <tr key={`${id.exchange || ''}-${id.marketType || ''}-${id.symbol || ''}-${id.timeFrame || ''}-${id.calculatedCandleCloseTime || ''}`}>
                  <td>{fmt(id.symbol)}</td>
                  <td>{fmt(id.timeFrame)}</td>
                  <td>{fmt(id.calculatedCandleCloseTime)}</td>
                  <td>{fmt(row.atr14)}</td>
                  <td>{fmt(row.atr14Percent)}</td>
                  <td>{fmt(row.open)}</td>
                  <td>{fmt(row.high)}</td>
                  <td>{fmt(row.low)}</td>
                  <td>{fmt(row.close)}</td>
                  <td>{fmt(row.volume)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="atr-values-footer">
        Showing {visibleRows.length} of {rows.length} records
      </div>
    </div>
  );
}

export default AtrIndicatorTable;
